import type { ResolvedTheme } from '../../~reusables/contexts/ThemeModeContext';

/*
  The v2 pages' colors in each theme. App.globalCSS sets them as --v2-*
  CSS variables, which `tokens` (ui.tsx) names, so styles follow the
  theme. Canvas charts can't read CSS variables: they resolve a token to
  its value with `resolveColor`.

  "Solid" colors (and blueStrong) are fills under white (onSolid) text
  and marks; the plain ones are text, icons and lines on the card.
*/
export const v2Palette = {
  light: {
    bg: '#F6F6F8',
    blue: '#3946F1',
    blueBorder: '#D7DAFB',
    blueDark: '#2733C9',
    blueSoft: '#EEF0FE',
    blueSolid: '#3946F1',
    blueStrong: '#2733C9',
    blueWash: '#FAFAFF',
    border: '#E7E7EC',
    borderStrong: '#D9D9E0',
    card: '#FFFFFF',
    faint: '#8A8A99',
    green: '#17693A',
    greenBorder: '#B9DCC7',
    greenSoft: '#E8F5EE',
    greenSolid: '#17693A',
    grey: '#45454F',
    greySoft: '#F0F0F3',
    inverse: '#16161D',
    inverseText: '#FFFFFF',
    line: '#C9C9D2',
    lineStrong: '#B5B5C0',
    muted: '#5E5E6B',
    onSolid: '#FFFFFF',
    orange: '#8A4B00',
    orangeBorder: '#F1D8A8',
    orangeBorderStrong: '#E9B872',
    orangeSoft: '#FFF3E0',
    orangeSolid: '#8A4B00',
    orangeWash: '#FFF8EC',
    red: '#A8231E',
    redBorder: '#F0C4C0',
    redSoft: '#FDECEA',
    redSolid: '#A8231E',
    redWash: '#FFF8F7',
    rowLine: '#F0F0F3',
    /** Behind a panel that covers the page while something runs. */
    scrim: 'rgba(22, 22, 29, 0.32)',
    shadow: 'rgba(22, 22, 29, 0.06)',
    subtle: '#FAFAFB',
    text: '#16161D',
    /** Chart series: newest build, previous, older, warning, other. */
    series1: '#3946F1',
    series2: '#A9B0F8',
    series3: '#D7DAFB',
    series4: '#E07A1F',
    series5: '#8A8A99',
    /** Status graphics (journey bars, dots): 3:1 against the card. */
    statusFailed: '#C8312B',
    statusPassed: '#2E9E5B',
    statusWarning: '#D98A1E',
    statusPaused: '#F2C08A',
    statusReached: '#C98A3D',
    statusExpiring: '#C2410C',
  },
  dark: {
    bg: '#222225',
    blue: '#8C95FF',
    blueBorder: '#3B4196',
    blueDark: '#B7BCFF',
    blueSoft: '#2A2D4A',
    blueSolid: '#4F5BF3',
    blueStrong: '#5A65F6',
    blueWash: '#272939',
    border: '#38383D',
    borderStrong: '#46464C',
    card: '#2A2A2A',
    faint: '#80808E',
    green: '#86EFAC',
    greenBorder: '#24513A',
    greenSoft: '#1B3326',
    greenSolid: '#1F7A45',
    grey: '#C8C8D0',
    greySoft: '#35353A',
    inverse: '#55555E',
    inverseText: '#F4F4F6',
    line: '#4A4A52',
    lineStrong: '#5E5E68',
    muted: '#A0A0AC',
    onSolid: '#FFFFFF',
    orange: '#FDBA74',
    orangeBorder: '#5A4220',
    orangeBorderStrong: '#7A5A2A',
    orangeSoft: '#3A2A14',
    orangeSolid: '#9A5408',
    orangeWash: '#2E2416',
    red: '#FCA5A5',
    redBorder: '#5C2629',
    redSoft: '#3A1D1E',
    redSolid: '#B42D27',
    redWash: '#2F1F1F',
    rowLine: '#333337',
    scrim: 'rgba(0, 0, 0, 0.55)',
    shadow: 'rgba(0, 0, 0, 0.3)',
    subtle: '#262629',
    text: '#E6E6E9',
    series1: '#8C95FF',
    series2: '#5C64C4',
    series3: '#3E4377',
    series4: '#E8893A',
    series5: '#5A5A66',
    statusFailed: '#EF5A52',
    statusPassed: '#3CB371',
    statusWarning: '#E3A03A',
    statusPaused: '#8A6532',
    statusReached: '#D69A4E',
    statusExpiring: '#F97316',
  },
} as const satisfies Record<ResolvedTheme, Record<string, string>>;

export type V2Color = keyof (typeof v2Palette)['light'];

const cssName = (name: string) =>
  `--v2-${name.replace(/[A-Z]|\d+/g, (part) => `-${part.toLowerCase()}`)}`;

/** `var(--v2-…)` for each color, for styles. */
export const v2Vars = Object.fromEntries(
  Object.keys(v2Palette.light).map((name) => [name, `var(${cssName(name)})`]),
) as Record<V2Color, string>;

/** The CSS variable declarations for a theme (App.globalCSS). */
export const v2CssVariables = (theme: ResolvedTheme): string =>
  Object.entries(v2Palette[theme])
    .map(([name, value]) => `${cssName(name)}: ${value};`)
    .join('\n');

const varPattern = /^var\(--v2-([a-z0-9-]+)\)$/;
const byCssName = Object.fromEntries(
  Object.keys(v2Palette.light).map((name) => [
    cssName(name).slice('--v2-'.length),
    name as V2Color,
  ]),
);

/** A token's value in a theme, for canvas; other colors pass through. */
export function resolveColor(color: string, theme: ResolvedTheme): string {
  const name = byCssName[varPattern.exec(color)?.[1] ?? ''];
  return name ? v2Palette[theme][name] : color;
}
